import { useState, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications, PermissionStatus } from '@capacitor/push-notifications';
import { useDeviceTokens } from './useDeviceTokens';
import { useAppName } from './useAppName';
import { useAuth } from '@/contexts/AuthContext';

export interface PushNotificationStatus {
  isNative: boolean;
  permissionStatus: 'granted' | 'denied' | 'prompt' | 'unknown';
  isRegistered: boolean;
}

export function usePushNotificationActions() {
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<PushNotificationStatus>({
    isNative: Capacitor.getPlatform() !== 'web',
    permissionStatus: 'unknown',
    isRegistered: false,
  });
  
  const { tokens, fetchTokens, registerToken } = useDeviceTokens();
  const appName = useAppName();
  const { user } = useAuth();

  const checkStatus = useCallback(async () => {
    if (Capacitor.getPlatform() === 'web') {
      setStatus({
        isNative: false,
        permissionStatus: 'unknown',
        isRegistered: false,
      });
      return;
    }

    try {
      const permResult = await PushNotifications.checkPermissions();
      await fetchTokens();
      
      setStatus({
        isNative: true,
        permissionStatus: permResult.receive as 'granted' | 'denied' | 'prompt',
        isRegistered: tokens.length > 0,
      });
    } catch (error) {
      console.error('Error checking push notification status:', error);
    }
  }, [fetchTokens, tokens.length]);

  const forceReRegister = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (Capacitor.getPlatform() === 'web') {
      return { success: false, error: 'Not available on web' };
    }

    if (!user?.id) {
      return { success: false, error: 'User not authenticated' };
    }

    setIsLoading(true);

    try {
      // 1. Remove all existing listeners
      await PushNotifications.removeAllListeners();
      console.log('🔄 Removed all push notification listeners');

      // 2. Request permissions
      const permResult = await PushNotifications.requestPermissions();
      console.log('📜 Permission result:', permResult);

      if (permResult.receive !== 'granted') {
        setIsLoading(false);
        return { success: false, error: 'Permission denied' };
      }

      // 3. Create a promise that resolves when we get the token
      const tokenPromise = new Promise<string>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Token registration timeout'));
        }, 10000);

        PushNotifications.addListener('registration', (token) => {
          clearTimeout(timeout);
          console.log('✅ New token received:', token.value);
          resolve(token.value);
        });

        PushNotifications.addListener('registrationError', (error) => {
          clearTimeout(timeout);
          console.error('❌ Registration error:', error);
          reject(new Error(error.error || 'Registration failed'));
        });
      });

      // 4. Trigger registration
      await PushNotifications.register();
      console.log('⚡️ PushNotifications.register() called');

      // 5. Wait for the token
      const newToken = await tokenPromise;

      // 6. Register the token in the database
      await registerToken(newToken, user.id, appName);
      console.log('✅ Token registered in database');

      // 7. Refresh status
      await fetchTokens();
      
      setStatus(prev => ({
        ...prev,
        permissionStatus: 'granted',
        isRegistered: true,
      }));

      setIsLoading(false);
      return { success: true };

    } catch (error: any) {
      console.error('Error during re-registration:', error);
      setIsLoading(false);
      return { success: false, error: error.message || 'Unknown error' };
    }
  }, [user?.id, appName, registerToken, fetchTokens]);

  return {
    status,
    tokens,
    isLoading,
    checkStatus,
    forceReRegister,
  };
}
